import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, User, Briefcase } from '@phosphor-icons/react';
import { toast } from 'sonner';

const AuthPage = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('candidate');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.email, formData.password, formData.full_name, role);
        toast.success('Account created successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-12">
            <h1 className="text-5xl font-black tracking-tighter leading-none mb-2" data-testid="auth-title">
              TalentoX
            </h1>
            <p className="text-base font-light text-[#52525B]">
              {isLogin ? 'Welcome back' : 'Start your journey'}
            </p>
          </div>

          {!isLogin && (
            <div className="mb-8">
              <label className="text-xs tracking-[0.2em] uppercase font-semibold mb-4 block">
                I am a
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setRole('candidate')}
                  className={`p-4 border transition-colors rounded-none ${
                    role === 'candidate'
                      ? 'border-[#0033FF] bg-[#0033FF] text-white'
                      : 'border-black/10 hover:bg-black/5'
                  }`}
                  data-testid="role-candidate-button"
                >
                  <User size={24} className="mx-auto mb-2" />
                  <div className="font-medium">Candidate</div>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('employer')}
                  className={`p-4 border transition-colors rounded-none ${
                    role === 'employer'
                      ? 'border-[#0033FF] bg-[#0033FF] text-white'
                      : 'border-black/10 hover:bg-black/5'
                  }`}
                  data-testid="role-employer-button"
                >
                  <Briefcase size={24} className="mx-auto mb-2" />
                  <div className="font-medium">Employer</div>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-semibold mb-2 block">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#F9F9FB] border border-black/10 rounded-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent outline-none transition-all"
                  required
                  data-testid="input-fullname"
                />
              </div>
            )}

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold mb-2 block">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-[#F9F9FB] border border-black/10 rounded-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent outline-none transition-all"
                required
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold mb-2 block">
                Password
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 bg-[#F9F9FB] border border-black/10 rounded-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent outline-none transition-all"
                required
                data-testid="input-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0033FF] text-white rounded-none font-medium px-6 py-3 hover:bg-[#002AE6] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-auth-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              <ArrowRight size={20} />
            </button>
          </form>

          <div className="mt-8 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm font-medium text-[#52525B] hover:text-[#0033FF] transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-white">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/68f8c0f8-9e08-45d3-9309-aad3baf14c1b/images/6340d6c3bc9eb07c659fee542dacf1629c3e747483b77c3756cec92c2291c640.png"
            alt="TalentoX AI"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
